import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevronRightSmall,
  AlertTriangle,
  ShieldAlert,
  CheckCircle2,
  RotateCcw,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { GET_ERROR_LOGS, GET_ERROR_LOG } from "@/graphql/queries/errors";
import {
  RESOLVE_ERROR_LOG,
  DELETE_ERROR_LOGS,
  COPY_ERROR_REPORT,
} from "@/graphql/mutations/errors";
import { CopyForAi } from "@/components/admin/copy-for-ai";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const PAGE_SIZE = 25;

type ErrorGroup = {
  id: string;
  exceptionClass: string;
  message: string | null;
  origin: string | null;
  handled: boolean;
  severity: string | null;
  source: string | null;
  context: Record<string, unknown> | null;
  occurrences: number;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  resolved: boolean;
  /** When this crash was last handed to an AI. Null means never. */
  copiedAt: string | null;
};

/**
 * "prieš 3 min" beats a timestamp here: the first question about an error is
 * whether it is still happening, and that is a distance rather than a date.
 */
function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "ką tik";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `prieš ${minutes} min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `prieš ${hours} val.`;
  const days = Math.floor(hours / 24);
  return `prieš ${days} d.`;
}

function absolute(iso: string): string {
  return new Intl.DateTimeFormat("lt-LT", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(new Date(iso));
}

/** The backtrace and context, fetched only for the row somebody opened. */
function ErrorDetail({ id }: { id: string }) {
  const { data, loading } = useQuery(GET_ERROR_LOG, { variables: { id } });
  const log: any = (data as any)?.errorLog;

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Kraunama…
      </div>
    );
  }
  if (!log) return null;

  const context = log.context ?? {};
  const contextKeys = Object.keys(context);

  return (
    <div className="space-y-4 py-2">
      <div>
        <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
          Pranešimas
        </div>
        <pre className="whitespace-pre-wrap break-words rounded bg-muted p-3 text-xs">
          {log.message || "—"}
        </pre>
      </div>

      {contextKeys.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
            Kontekstas
          </div>
          <div className="flex flex-wrap gap-2">
            {contextKeys.map((key) => (
              <Badge key={key} variant="outline" className="font-mono text-xs">
                {key}: {String(context[key])}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
          Kelias iki klaidos
        </div>
        {log.backtrace?.length ? (
          <pre className="max-h-80 overflow-auto rounded bg-muted p-3 font-mono text-xs leading-relaxed">
            {log.backtrace.join("\n")}
          </pre>
        ) : (
          <div className="text-sm text-muted-foreground">Nėra</div>
        )}
      </div>

      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span>Pirmą kartą: {absolute(log.firstSeenAt)}</span>
        <span>Paskutinį kartą: {absolute(log.lastSeenAt)}</span>
        {log.source && <span>Šaltinis: {log.source}</span>}
        {log.severity && <span>Lygis: {log.severity}</span>}
      </div>
    </div>
  );
}

/**
 * Klaidų stebėjimas.
 *
 * One row is one *kind* of error with a count, not one occurrence — the learner
 * shell polls `Me` every 30 seconds, so a crash in a polled resolver would
 * otherwise push every other error off the page within a minute.
 */
export default function ErrorsPage() {
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("unresolved");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(GET_ERROR_LOGS, {
    variables: {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      status,
      search: search.trim() || undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const [resolveErrorLog] = useMutation(RESOLVE_ERROR_LOG);
  const [deleteErrorLogs, { loading: isDeleting }] = useMutation(DELETE_ERROR_LOGS);

  const logs: ErrorGroup[] = (data as any)?.errorLogs?.logs ?? [];
  const total = (data as any)?.errorLogs?.total ?? 0;
  const stats = (data as any)?.errorLogs?.stats ?? null;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const toggleResolved = async (log: ErrorGroup) => {
    try {
      const result: any = await resolveErrorLog({
        variables: { id: log.id, resolved: !log.resolved },
      });
      const errors = result?.data?.resolveErrorLog?.errors ?? [];
      if (errors.length) {
        toast.error(errors[0]);
        return;
      }
      toast.success(log.resolved ? "Grąžinta į sąrašą" : "Pažymėta kaip išspręsta");
      refetch();
    } catch {
      toast.error("Nepavyko pakeisti būsenos");
    }
  };

  const clearResolved = async () => {
    try {
      const result: any = await deleteErrorLogs({ variables: { scope: "resolved" } });
      const errors = result?.data?.deleteErrorLogs?.errors ?? [];
      if (errors.length) {
        toast.error(errors[0]);
        return;
      }
      toast.success(
        `Ištrinta ${result?.data?.deleteErrorLogs?.deletedCount ?? 0} išspręstų klaidų`
      );
      refetch();
    } catch {
      toast.error("Nepavyko ištrinti");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Klaidos</h1>
          <p className="text-muted-foreground">
            Kas nulūžo programoje. Viena eilutė — viena klaidos rūšis, ne vienas
            atvejis
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/*
            Copies what is on screen, not the whole table: the same status and
            search the list is filtered by, so the paste and the page describe
            the same crashes. How many of them it holds is stated in the report
            itself — a bounded paste that says nothing reads as "all of them".
          */}
          <CopyForAi
            document={COPY_ERROR_REPORT}
            pick={(data) => data?.copyErrorReport}
            variables={{ status, search: search.trim() || undefined }}
            label="Kopijuoti AI"
            hint="Rodomas klaidas su keliais — įklijuok pokalbyje su AI"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Atnaujinti
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={isDeleting}>
                <Trash2 className="h-4 w-4 mr-2" />
                Valyti išspręstas
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Ištrinti išspręstas klaidas?</AlertDialogTitle>
                <AlertDialogDescription>
                  Bus ištrintos visos kaip išspręstos pažymėtos klaidos su jų
                  istorija. Jei tokia klaida pasikartos, ji atsiras iš naujo — su
                  skaitliuku nuo vieno.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Atšaukti</AlertDialogCancel>
                <AlertDialogAction onClick={clearResolved}>Ištrinti</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Neišspręstų rūšių</div>
            <div className="text-2xl font-bold">{stats.unresolvedGroups}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Pasiekė naudotoją</div>
            <div
              className={`text-2xl font-bold ${
                stats.unhandledGroups > 0 ? "text-red-600" : ""
              }`}
            >
              {stats.unhandledGroups}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Vis dar kartojasi</div>
            <div className="text-2xl font-bold">{stats.activeLastDay}</div>
            <div className="text-xs text-muted-foreground">per 24 val.</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Viso atvejų</div>
            <div className="text-2xl font-bold">{stats.totalOccurrences}</div>
            <div className="text-xs text-muted-foreground">
              saugoma {stats.retentionDays} d. po paskutinio karto
            </div>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-56">
              <Select
                value={status}
                onValueChange={(value) => {
                  setStatus(value);
                  setPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unresolved">Neišspręstos</SelectItem>
                  <SelectItem value="unhandled">Pasiekusios naudotoją</SelectItem>
                  <SelectItem value="resolved">Išspręstos</SelectItem>
                  <SelectItem value="all">Visos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[16rem] flex-1">
              <Input
                placeholder="Ieškoti klasėje, pranešime, kelyje…"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Klaidų sąrašas</CardTitle>
          <CardDescription>
            Rodoma {logs.length} iš {total}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && logs.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-green-600" />
              <div className="font-medium">
                {status === "unresolved"
                  ? "Nė vienos neišspręstos klaidos"
                  : "Nieko nerasta"}
              </div>
              <div className="text-sm text-muted-foreground">
                {status === "unresolved"
                  ? "Programa nieko neužfiksavo — arba viskas jau sutvarkyta"
                  : "Pabandyk kitą filtrą"}
              </div>
            </div>
          ) : (
            <>
              <div className="divide-y">
                {logs.map((log) => {
                  const isOpen = openId === log.id;
                  return (
                    <div key={log.id} className="py-3">
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          onClick={() => setOpenId(isOpen ? null : log.id)}
                          className="mt-0.5 text-muted-foreground hover:text-foreground"
                          aria-label={isOpen ? "Suskleisti" : "Išskleisti"}
                        >
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRightSmall className="h-4 w-4" />
                          )}
                        </button>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-sm font-semibold">
                              {log.exceptionClass}
                            </span>
                            {log.handled ? (
                              <Badge
                                variant="outline"
                                className="border-amber-300 text-amber-700 text-xs"
                              >
                                <AlertTriangle className="mr-1 h-3 w-3" />
                                Apeita
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="text-xs">
                                <ShieldAlert className="mr-1 h-3 w-3" />
                                Pasiekė naudotoją
                              </Badge>
                            )}
                            <Badge variant="secondary" className="text-xs">
                              {log.occurrences}×
                            </Badge>
                            {log.resolved && (
                              <Badge
                                variant="outline"
                                className="border-green-300 text-green-700 text-xs"
                              >
                                Išspręsta
                              </Badge>
                            )}
                          </div>

                          <div className="mt-1 truncate text-sm text-muted-foreground">
                            {log.message || "—"}
                          </div>

                          {log.origin && (
                            <div className="mt-1 truncate font-mono text-xs text-muted-foreground/80">
                              {log.origin}
                            </div>
                          )}

                          <div className="mt-1 text-xs text-muted-foreground">
                            {ago(log.lastSeenAt)}
                            {log.occurrences > 1 && (
                              <> · nuo {absolute(log.firstSeenAt)}</>
                            )}
                            {log.context?.operation ? (
                              <> · {String(log.context.operation)}</>
                            ) : log.context?.path ? (
                              <> · {String(log.context.path)}</>
                            ) : null}
                          </div>
                        </div>

                        {/* Icon-only and always visible: the hover is where
                            "already handed over" is read, so it must not be
                            behind expanding the row. */}
                        <CopyForAi
                          document={COPY_ERROR_REPORT}
                          pick={(data) => data?.copyErrorReport}
                          variables={{ id: log.id }}
                          hint="Kopijuoti AI — šią klaidą su visu keliu"
                          copiedAt={log.copiedAt}
                          variant="ghost"
                          size="xs"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleResolved(log)}
                          title={
                            log.resolved
                              ? "Grąžinti į sąrašą"
                              : "Pažymėti kaip išspręstą"
                          }
                        >
                          {log.resolved ? (
                            <RotateCcw className="h-4 w-4" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>

                      {isOpen && (
                        <div className="pl-7">
                          <ErrorDetail id={log.id} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Puslapis {page + 1} iš {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Ankstesnis
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      Kitas
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
